import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { useFonts } from 'expo-font';
import { Lora_700Bold } from '@expo-google-fonts/lora';
import {
  DMSans_400Regular,
  DMSans_500Medium,
  DMSans_600SemiBold,
  DMSans_700Bold,
} from '@expo-google-fonts/dm-sans';
import { useAuth } from '../../lib/contexts/AuthContext';
import { DynamicTitle } from '../DynamicTitle';

export function ComensalLoginPage() {
  const navigate = useNavigate();
  const [fontsLoaded] = useFonts({
    Lora_700Bold,
    DMSans_400Regular,
    DMSans_500Medium,
    DMSans_600SemiBold,
    DMSans_700Bold,
  });
  const { profile, loading: authLoading, signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Esta ruta es solo comensales: si ya hay sesión, siempre el área /app (el staff usa /login).
  useEffect(() => {
    if (authLoading || !profile) return;
    navigate('/', { replace: true });
  }, [authLoading, profile, navigate]);

  const handleLogin = async () => {
    setFormError(null);
    if (!email.trim() || !password.trim()) {
      setFormError('Completa correo y contraseña');
      return;
    }
    setLoading(true);
    try {
      await signIn(email.trim(), password);
      navigate('/', { replace: true });
    } catch (error: any) {
      const msg = (error?.message ?? error?.error_description ?? String(error ?? '')).toLowerCase();
      const isInvalid =
        error?.name === 'AuthApiError' ||
        msg.includes('invalid login') ||
        msg.includes('invalid_credentials') ||
        msg.includes('credenciales');
      setFormError(
        isInvalid ? 'Correo o contraseña incorrectos' : (error?.message || 'No se pudo iniciar sesión.')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOlvideContrasena = () => {
    if (!email.trim()) {
      setFormError('Indica tu correo electrónico para recuperar la contraseña');
      return;
    }
    setFormError(null);
    navigate(`/recuperar-contrasena?email=${encodeURIComponent(email.trim())}`);
  };

  if (authLoading || !fontsLoaded) {
    return (
      <View style={styles.loader}>
        <ActivityIndicator size="large" color="#030213" />
      </View>
    );
  }

  return (
    <KeyboardAvoidingView style={styles.flex} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <DynamicTitle title="Entrar — As Gastronómico" />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.scroll}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.header}>
          <Text style={styles.screenTitle}>Iniciar Sesión</Text>
          <Text style={styles.subtitle}>
            Ingresa tus datos para iniciar sesión en tu cuenta de comensal
          </Text>
        </View>

        {formError ? (
          <View style={styles.errorBanner}>
            <Ionicons name="alert-circle" size={20} color="#dc2626" style={styles.errorIcon} />
            <Text style={styles.errorText}>{formError}</Text>
          </View>
        ) : null}

        <View style={styles.form}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Correo electrónico</Text>
            <View style={styles.inputContainer}>
              <Ionicons name="mail-outline" size={20} color="#6b7280" style={styles.inputIcon} />
              <TextInput
                style={styles.input}
                placeholder="tu@email.com"
                placeholderTextColor="#9ca3af"
                value={email}
                onChangeText={(t) => {
                  setEmail(t);
                  if (formError) setFormError(null);
                }}
                autoCapitalize="none"
                autoCorrect={false}
                keyboardType="email-address"
                editable={!loading}
              />
            </View>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Contraseña</Text>
            <View style={styles.inputContainer}>
              <Ionicons name="lock-closed-outline" size={20} color="#6b7280" style={styles.inputIcon} />
              <TextInput
                style={[styles.input, styles.passwordInput]}
                placeholder="••••••••"
                placeholderTextColor="#9ca3af"
                value={password}
                onChangeText={(t) => {
                  setPassword(t);
                  if (formError) setFormError(null);
                }}
                secureTextEntry={!showPassword}
                autoCapitalize="none"
                autoCorrect={false}
                editable={!loading}
              />
              <TouchableOpacity style={styles.eyeIcon} onPress={() => setShowPassword(!showPassword)} disabled={loading}>
                <Ionicons name={showPassword ? 'eye-off-outline' : 'eye-outline'} size={20} color="#6b7280" />
              </TouchableOpacity>
            </View>
          </View>

          <TouchableOpacity
            style={styles.forgotPasswordWrap}
            onPress={handleOlvideContrasena}
            disabled={loading}
            activeOpacity={0.7}
          >
            <Text style={styles.forgotPasswordText}>¿Olvidaste la contraseña?</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.primaryButton, loading && styles.primaryDisabled]}
            onPress={handleLogin}
            disabled={loading}
            activeOpacity={0.85}
          >
            {loading ? (
              <ActivityIndicator color="#ffffff" />
            ) : (
              <Text style={styles.primaryButtonText}>Entrar</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity style={styles.secondaryWrap} onPress={() => navigate('/registro')} disabled={loading}>
            <Text style={styles.secondaryText}>¿No tienes cuenta? </Text>
            <Text style={styles.secondaryLink}>Registrarse</Text>
          </TouchableOpacity>

          <Text style={styles.staffHint}>
            ¿Eres del equipo As Gastronómico? Usa el acceso interno.
          </Text>
          <TouchableOpacity onPress={() => navigate('/login')} disabled={loading}>
            <Text style={styles.staffLink}>Ir al panel de administración</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  loader: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  scroll: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 12,
  },
  header: {
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    marginBottom: 28,
    minHeight: 100,
    justifyContent: 'center',
  },
  screenTitle: {
    fontFamily: 'DMSans_700Bold',
    fontSize: 28,
    color: '#030213',
    textAlign: 'left',
    alignSelf: 'stretch',
    marginBottom: 10,
    letterSpacing: -0.3,
  },
  subtitle: {
    fontFamily: 'DMSans_400Regular',
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'left',
    lineHeight: 22,
    alignSelf: 'stretch',
  },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
    borderRadius: 8,
    padding: 14,
    marginBottom: 20,
  },
  errorIcon: {
    marginRight: 10,
    marginTop: 2,
  },
  errorText: {
    fontFamily: 'DMSans_500Medium',
    flex: 1,
    fontSize: 14,
    color: '#dc2626',
  },
  form: {
    width: '100%',
  },
  inputGroup: {
    marginBottom: 18,
  },
  label: {
    fontFamily: 'DMSans_600SemiBold',
    fontSize: 14,
    color: '#030213',
    marginBottom: 8,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f9fafb',
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    paddingHorizontal: 12,
  },
  inputIcon: {
    marginRight: 8,
  },
  input: {
    fontFamily: 'DMSans_400Regular',
    flex: 1,
    fontSize: 16,
    color: '#030213',
    paddingVertical: 12,
    minWidth: 0,
  },
  passwordInput: {
    paddingRight: 36,
  },
  eyeIcon: {
    padding: 4,
  },
  forgotPasswordWrap: {
    alignSelf: 'flex-end',
    marginBottom: 4,
    marginTop: -8,
    paddingVertical: 6,
  },
  forgotPasswordText: {
    fontFamily: 'Lora_700Bold',
    fontSize: 14,
    color: '#A20000',
    textDecorationLine: 'underline',
  },
  primaryButton: {
    backgroundColor: '#A20000',
    borderRadius: 8,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 8,
  },
  primaryDisabled: {
    opacity: 0.65,
  },
  primaryButtonText: {
    fontFamily: 'Lora_700Bold',
    color: '#ffffff',
    fontSize: 16,
  },
  secondaryWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: 22,
  },
  secondaryText: {
    fontFamily: 'DMSans_400Regular',
    fontSize: 15,
    color: '#4b5563',
  },
  secondaryLink: {
    fontFamily: 'Lora_700Bold',
    fontSize: 15,
    color: '#A20000',
  },
  staffHint: {
    fontFamily: 'DMSans_400Regular',
    marginTop: 32,
    fontSize: 13,
    color: '#9ca3af',
    textAlign: 'center',
    lineHeight: 18,
  },
  staffLink: {
    fontFamily: 'Lora_700Bold',
    marginTop: 8,
    fontSize: 14,
    color: '#6b7280',
    textAlign: 'center',
    textDecorationLine: 'underline',
  },
});
